import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import http from '../api/http'; // 引入封装的axios实例
import { useToasts } from '../context/ToastContext';
// Modal 组件未使用（原考试系统标签移除后），删除多余导入
import AnnualPlanForm from './AnnualPlanForm'; // 引入表单组件
import WideModal from '../components/WideModal';
import SpecialOperatorForm from './SpecialOperatorForm';
import SafetyManagerForm from './SafetyManagerForm';
import SimplePdfViewer from '../components/SimplePdfViewer';
import TrainingPlansTab from './TrainingPlansTab';
import OnJobTrainingLedgerTab from './OnJobTrainingLedgerTab';
// 考试系统已独立为左侧菜单入口，移除内部标签引用

// --- 子组件 ---

const formatPercent = (value) => {
  if (!Number.isFinite(value)) return '--';
  const pct = (value * 100);
  return (pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(1)) + '%';
};

const STAGE_CONFIG = [
  { key: 'project', label: '项目部教育', shortLabel: '项目部', timeField: 'project_training_time', trainerField: 'project_trainer', scoreField: 'project_score', color: '#2563eb', softColor: 'rgba(37,99,235,0.12)' },
  { key: 'company', label: '公司级教育', shortLabel: '公司级', timeField: 'company_training_time', trainerField: 'company_trainer', scoreField: 'company_score', color: '#0ea5e9', softColor: 'rgba(14,165,233,0.12)' },
  { key: 'site', label: '厂级教育', shortLabel: '厂级', timeField: 'site_training_time', trainerField: 'site_trainer', scoreField: 'site_score', color: '#22c55e', softColor: 'rgba(34,197,94,0.12)' },
  { key: 'team', label: '班组级教育', shortLabel: '班组级', timeField: 'team_training_time', trainerField: 'team_trainer', scoreField: 'team_score', color: '#f59e0b', softColor: 'rgba(245,158,11,0.12)' }
];

const TRAINING_ENTRUST_STORAGE_KEY = 'trainingEntrustRecords';

const DEFAULT_LEDGER_FILTERS = {
  name: '',
  department: '',
  section: '',
  startDate: '',
  endDate: ''
};

const LEDGER_EMPTY_OPTION_VALUE = '__EMPTY_LEDGER_OPTION__';

const formatCardDate = (value) => {
  if (!value) return null;
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) return value;
  return new Date(ts).toISOString().slice(0, 10);
};

const buildSafetyLedgerFormState = (record = {}) => {
  const source = record && typeof record === 'object' ? record : {};
  const state = {
    employee_name: source.employee_name || source.employee_name_official || '',
    employee_id: source.employee_id !== undefined && source.employee_id !== null ? String(source.employee_id) : '',
    import_batch: source.import_batch || '',
    sequence_number: source.sequence_number !== undefined && source.sequence_number !== null ? String(source.sequence_number) : '',
    gender: source.gender || '',
    department_name: source.department_name || '',
    plant_area: source.plant_area || '',
    job_title: source.job_title || '',
    education_level: source.education_level || ''
  };
  STAGE_CONFIG.forEach((stage) => {
    const rawDate = source[stage.timeField];
    state[stage.timeField] = rawDate ? String(rawDate).slice(0, 10) : '';
    state[stage.trainerField] = source[stage.trainerField] || '';
    state[stage.scoreField] = source[stage.scoreField] || '';
  });
  return state;
};

const SafetyLedgerForm = ({ record, mode, onSubmit, onCancel, submitting }) => {
  const [form, setForm] = useState(buildSafetyLedgerFormState(record));

  useEffect(() => {
    setForm(buildSafetyLedgerFormState(record));
  }, [record]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(form);
  };

  const infoGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12 };
  const labelStyle = { fontSize: 12, color: '#64748b' };
  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 13, background: '#fff' };
  const stageGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 };
  const stageCardStyle = { border: '1px solid var(--border-color)', borderRadius: 12, padding: '12px 14px', background: '#fff', display: 'flex', flexDirection: 'column', gap: 10, boxShadow: '0 4px 12px rgba(15,23,42,0.05)' };
  const stageLabelStyle = (color) => ({ fontSize: 13, fontWeight: 600, color });

  const showRecordSummary = mode === 'edit' && record;

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {showRecordSummary && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>受教育员工</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{record.employee_name || record.employee_name_official || '—'}</div>
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>导入批次：{record.import_batch || '未标记批次'}</div>
        </div>
      )}
      <div style={infoGridStyle}>
        <div>
          <div style={labelStyle}>员工姓名</div>
          <input
            name="employee_name"
            value={form.employee_name}
            onChange={handleInputChange}
            style={inputStyle}
            placeholder="必填"
            required
          />
        </div>
        <div>
          <div style={labelStyle}>员工编号</div>
          <input
            name="employee_id"
            value={form.employee_id}
            onChange={handleInputChange}
            style={inputStyle}
            placeholder="可填写员工ID"
            type="number"
            min="1"
          />
        </div>
        <div>
          <div style={labelStyle}>导入批次</div>
          <input
            name="import_batch"
            value={form.import_batch}
            onChange={handleInputChange}
            style={inputStyle}
            placeholder="如 2025Q1"
          />
        </div>
        <div>
          <div style={labelStyle}>序号</div>
          <input name="sequence_number" value={form.sequence_number} onChange={handleInputChange} style={inputStyle} placeholder="可输入数字" />
        </div>
        <div>
          <div style={labelStyle}>性别</div>
          <input name="gender" value={form.gender} onChange={handleInputChange} style={inputStyle} placeholder="男 / 女" />
        </div>
        <div>
          <div style={labelStyle}>部门</div>
          <input name="department_name" value={form.department_name} onChange={handleInputChange} style={inputStyle} placeholder="所属部门" />
        </div>
        <div>
          <div style={labelStyle}>科室/区域</div>
          <input name="plant_area" value={form.plant_area} onChange={handleInputChange} style={inputStyle} placeholder="填写科室或区域" />
        </div>
        <div>
          <div style={labelStyle}>岗位</div>
          <input name="job_title" value={form.job_title} onChange={handleInputChange} style={inputStyle} placeholder="岗位信息" />
        </div>
        <div>
          <div style={labelStyle}>文化程度</div>
          <input name="education_level" value={form.education_level} onChange={handleInputChange} style={inputStyle} placeholder="如 高中 / 专科" />
        </div>
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 10 }}>培训阶段信息</div>
        <div style={stageGridStyle}>
          {STAGE_CONFIG.map((stage) => (
            <div key={stage.key} style={stageCardStyle}>
              <div style={stageLabelStyle(stage.color)}>{stage.label}</div>
              <div>
                <div style={labelStyle}>培训时间</div>
                <input
                  type="date"
                  name={stage.timeField}
                  value={form[stage.timeField]}
                  onChange={handleInputChange}
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={labelStyle}>授课人</div>
                <input
                  name={stage.trainerField}
                  value={form[stage.trainerField]}
                  onChange={handleInputChange}
                  style={inputStyle}
                  placeholder="填写授课人"
                />
              </div>
              <div>
                <div style={labelStyle}>成绩</div>
                <input
                  name={stage.scoreField}
                  value={form[stage.scoreField]}
                  onChange={handleInputChange}
                  style={inputStyle}
                  placeholder="可填写分数或等级"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <button
          type="button"
          onClick={onCancel}
          style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border-color)', background: '#fff', color: 'var(--text-primary)', fontSize: 13 }}
        >取消</button>
        <button type="submit" style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, minWidth: 120 }} disabled={submitting}>
          {submitting ? '保存中...' : (mode === 'edit' ? '保存修改' : '保存台账')}
        </button>
      </div>
    </form>
  );
};

const SafetyLedgerTable = ({ records, activeFilter, onEdit, onDelete }) => {
  if (!records || records.length === 0) {
    return <div style={{ padding: '12px 0', color: 'var(--text-secondary)', fontSize: 13 }}>暂未导入安全培训台账数据。</div>;
  }

  const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: '12px' };
  const cardStyle = {
    background: '#fff',
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'var(--border-color)',
    padding: '10px 12px',
    boxShadow: '0 4px 12px rgba(15,23,42,0.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    transition: 'box-shadow .2s, transform .2s, border-color .2s'
  };
  const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' };
  const primaryInfoStyle = { display: 'flex', flexDirection: 'column', gap: 3 };
  const nameStyle = { fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' };
  const tagWrapStyle = { display: 'flex', flexWrap: 'wrap', gap: 4 };
  const tagStyle = { fontSize: 10, padding: '2px 6px', borderRadius: 999, background: '#eef2ff', color: '#1d4ed8' };
  const stageGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 6 };
  const stageHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 9, color: '#475569' };
  const stageLabelStyle = { fontWeight: 600, fontSize: 9 };
  const stageDateStyle = { fontSize: 9 };
  const stageFooterStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 9, color: '#64748b' };
  const stageScoreStyle = { fontSize: 13, fontWeight: 600, color: '#0f172a' };
  const actionBarStyle = { display: 'flex', justifyContent: 'flex-end', gap: 6 };
  const actionButtonStyle = {
    border: '1px solid var(--border-color)',
    background: '#fff',
    color: 'var(--text-primary)',
    borderRadius: 18,
    padding: '3px 8px',
    fontSize: 10,
    cursor: 'pointer',
    transition: 'border-color .2s, color .2s, box-shadow .2s'
  };

  return (
    <div style={gridStyle}>
      {records.map((item) => {
        const cardKey = item.rawId || item.id || `${item.employee_name || 'record'}-${item.sequence_number || 'n'}`;
        const departmentName = item.department_name || '未填写部门';
        const isDepartmentFocused = activeFilter?.type === 'department' && activeFilter.value === departmentName;
        const dynamicCardStyle = {
          ...cardStyle,
          borderColor: isDepartmentFocused ? '#2563eb' : cardStyle.borderColor,
          boxShadow: isDepartmentFocused ? '0 12px 28px rgba(37,99,235,0.18)' : cardStyle.boxShadow,
          transform: isDepartmentFocused ? 'translateY(-2px)' : 'none'
        };

        return (
          <div key={cardKey} style={dynamicCardStyle}>
            <div style={headerStyle}>
              <div style={primaryInfoStyle}>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>序号 {item.sequence_number ?? '-'}</span>
                <span style={nameStyle}>{item.employee_name || item.employee_name_official || '-'}</span>
              </div>
              <div style={{ textAlign: 'right', fontSize: 11, color: '#94a3b8', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span>{item.gender || '-'}</span>
              </div>
            </div>
            {(onEdit || onDelete) && (
              <div style={actionBarStyle}>
                {onEdit && (
                  <button
                    type="button"
                    style={actionButtonStyle}
                    onClick={() => onEdit(item)}
                    title="编辑台账"
                  >编辑</button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    style={{ ...actionButtonStyle, color: '#b91c1c', borderColor: '#fecaca' }}
                    onClick={() => onDelete(item)}
                    title="删除台账"
                  >删除</button>
                )}
              </div>
            )}
            <div style={tagWrapStyle}>
              <span style={tagStyle}>{departmentName}</span>
              {item.plant_area ? <span style={{ ...tagStyle, background: '#fef3c7', color: '#92400e' }}>{item.plant_area}</span> : null}
              {item.job_title ? <span style={{ ...tagStyle, background: '#dcfce7', color: '#15803d' }}>{item.job_title}</span> : null}
            </div>
            <div style={stageGridStyle}>
              {STAGE_CONFIG.map((stage) => {
                const completed = Boolean(item[stage.timeField]);
                const isStageFocused = activeFilter?.type === 'stage' && activeFilter.value === stage.key;
                const dateLabel = completed ? formatCardDate(item[stage.timeField]) : '待补';
                const score = item[stage.scoreField];
                const baseBorderColor = 'rgba(148,163,184,0.35)';
                const blockStyle = {
                  borderRadius: 8,
                  padding: '7px 9px',
                  background: completed ? stage.softColor : '#f8fafc',
                  border: `1px solid ${isStageFocused ? stage.color : baseBorderColor}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 5,
                  minHeight: 58,
                  boxShadow: isStageFocused ? `0 0 0 2px ${stage.softColor}` : 'none',
                  transform: isStageFocused ? 'translateY(-1px)' : 'none',
                  transition: 'border-color .2s, transform .2s, box-shadow .2s'
                };

                return (
                  <div key={stage.key} style={blockStyle}>
                    <div style={stageHeaderStyle}>
                      <span style={{ ...stageLabelStyle, color: stage.color }}>{stage.shortLabel}</span>
                      <span style={{ ...stageDateStyle, color: completed ? '#0f172a' : '#94a3b8' }}>{dateLabel}</span>
                    </div>
                    <div style={{ height: 4, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: completed ? '100%' : '12%', background: stage.color, opacity: completed ? 1 : 0.35 }} />
                    </div>
                    <div style={stageFooterStyle}>
                      {completed ? <span>成绩</span> : <span>尚未完成</span>}
                      <span style={stageScoreStyle}>{completed ? (score || '--') : '--'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const getExpiryMeta = (expiryDate) => {
  if (!expiryDate) {
    return { label: '有效期未填', style: { color: 'var(--text-secondary)' }, rowAccent: '#f1f5f9' };
  }
  const today = new Date();
  const expiry = new Date(expiryDate);
  if (Number.isNaN(expiry.getTime())) {
    return { label: '日期异常', style: { color: 'var(--danger-color)', fontWeight: 'bold' }, rowAccent: '#fee2e2' };
  }
  const daysDiff = Math.round((expiry - today) / (1000 * 60 * 60 * 24));
  if (daysDiff < 0) {
    return { label: `已过期 ${Math.abs(daysDiff)} 天`, style: { color: 'var(--danger-color)', fontWeight: 'bold' }, rowAccent: '#fee2e2' };
  }
  if (daysDiff <= 60) {
    return { label: `即将到期（${daysDiff}天）`, style: { color: 'var(--warning-color)', fontWeight: 'bold' }, rowAccent: '#fef3c7' };
  }
  return { label: `有效（${daysDiff}天）`, style: { color: '#047857', fontWeight: 600 }, rowAccent: null };
};

// 特种设备作业人员表格
const SpecialEquipTable = ({ operators, onEdit, onDelete, onPreview }) => {
  const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
  const thStyle = { background: 'var(--bg-primary)', padding: '12px 15px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--border-color)' };
  const tdStyle = { padding: '12px 15px', borderBottom: '1px solid var(--border-color)' };
  const actionBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--brand-primary)', marginRight: 10, fontSize: 13 };

  if (operators.length === 0) {
    return <div style={{ border: '2px dashed var(--border-color)', borderRadius: 8, padding: '1.25rem', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-primary)', fontSize: 13 }}>暂无特种设备作业人员记录。</div>;
  }

  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={thStyle}>姓名</th>
          <th style={thStyle}>作业类别</th>
          <th style={thStyle}>证书编号</th>
          <th style={thStyle}>发证日期</th>
          <th style={thStyle}>有效期至</th>
          <th style={thStyle}>到期状态</th>
          <th style={{ ...thStyle, width: 140 }}>证件 PDF</th>
          <th style={{ ...thStyle, width: 160 }}>操作</th>
        </tr>
      </thead>
      <tbody>
        {operators.map(op => {
          const expiryMeta = getExpiryMeta(op.expiry_date);
          const rowStyle = expiryMeta.rowAccent ? { background: expiryMeta.rowAccent } : undefined;
          return (
            <tr key={op.id} style={rowStyle}>
              <td style={tdStyle}>{op.employee_name || 'N/A'}</td>
              <td style={tdStyle}>{op.operation_type}</td>
              <td style={tdStyle}>{op.certificate_number}</td>
              <td style={tdStyle}>{op.issue_date ? new Date(op.issue_date).toLocaleDateString() : '-'}</td>
              <td style={tdStyle}>{op.expiry_date ? new Date(op.expiry_date).toLocaleDateString() : '-'}</td>
              <td style={{ ...tdStyle, ...expiryMeta.style }}>{expiryMeta.label}</td>
              <td style={tdStyle}>
                {op.certificate_url ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <button
                      style={{ ...actionBtnStyle, padding: 0, alignSelf: 'flex-start' }}
                      onClick={() => onPreview(op)}
                    >预览</button>
                    <a
                      href={op.certificate_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--brand-primary)', fontSize: 13 }}
                    >下载</a>
                  </div>
                ) : (
                  <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>未上传</span>
                )}
              </td>
              <td style={tdStyle}>
                <button style={actionBtnStyle} onClick={() => onEdit(op)}>编辑</button>
                <button style={{ ...actionBtnStyle, color: 'var(--danger-color)' }} onClick={() => onDelete(op.id)}>删除</button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// 安全管理人员持证表格
const SafetyManagerTable = ({ records, onEdit, onDelete, onPreview }) => {
  const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
  const thStyle = { background: 'var(--bg-primary)', padding: '12px 15px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--border-color)' };
  const tdStyle = { padding: '12px 15px', borderBottom: '1px solid var(--border-color)' };
  const actionBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--brand-primary)', marginRight: 10, fontSize: 13 };

  if (records.length === 0) {
    return <div style={{ border: '2px dashed var(--border-color)', borderRadius: 8, padding: '1.25rem', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--bg-primary)', fontSize: 13 }}>暂无安全管理人员持证记录。</div>;
  }

  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={thStyle}>姓名</th>
          <th style={thStyle}>管理岗位</th>
          <th style={thStyle}>证书编号</th>
          <th style={thStyle}>发证日期</th>
          <th style={thStyle}>有效期至</th>
          <th style={thStyle}>到期状态</th>
          <th style={{ ...thStyle, width: 140 }}>证件 PDF</th>
          <th style={{ ...thStyle, width: 160 }}>操作</th>
        </tr>
      </thead>
      <tbody>
        {records.map(item => {
          const expiryMeta = getExpiryMeta(item.expiry_date);
          const rowStyle = expiryMeta.rowAccent ? { background: expiryMeta.rowAccent } : undefined;
          return (
            <tr key={item.id} style={rowStyle}>
              <td style={tdStyle}>{item.employee_name || 'N/A'}</td>
              <td style={tdStyle}>{item.management_role}</td>
              <td style={tdStyle}>{item.certificate_number}</td>
              <td style={tdStyle}>{item.issue_date ? new Date(item.issue_date).toLocaleDateString() : '-'}</td>
              <td style={tdStyle}>{item.expiry_date ? new Date(item.expiry_date).toLocaleDateString() : '-'}</td>
              <td style={{ ...tdStyle, ...expiryMeta.style }}>{expiryMeta.label}</td>
              <td style={tdStyle}>
                {item.certificate_url ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <button
                      style={{ ...actionBtnStyle, padding: 0, alignSelf: 'flex-start' }}
                      onClick={() => onPreview(item)}
                    >预览</button>
                    <a
                      href={item.certificate_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--brand-primary)', fontSize: 13 }}
                    >下载</a>
                  </div>
                ) : (
                  <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>未上传</span>
                )}
              </td>
              <td style={tdStyle}>
                <button style={actionBtnStyle} onClick={() => onEdit(item)}>编辑</button>
                <button style={{ ...actionBtnStyle, color: 'var(--danger-color)' }} onClick={() => onDelete(item.id)}>删除</button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// 培训计划列表组件
const TrainingPlanList = ({ plans }) => {
  if (!plans || !plans.length) return null;
  return (
    <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', margin: '24px 0', overflow: 'hidden' }}>
      <h3 style={{margin:'16px 0 8px 24px', color:'#111827'}}>培训计划列表</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{padding:'12px 16px',background:'#f9fafb'}}>序号</th>
            <th style={{padding:'12px 16px',background:'#f9fafb'}}>培训人员</th>
            <th style={{padding:'12px 16px',background:'#f9fafb'}}>培训内容</th>
            <th style={{padding:'12px 16px',background:'#f9fafb'}}>培训方式</th>
            <th style={{padding:'12px 16px',background:'#f9fafb'}}>培训时间</th>
            <th style={{padding:'12px 16px',background:'#f9fafb'}}>学时</th>
            <th style={{padding:'12px 16px',background:'#f9fafb'}}>参培对象</th>
          </tr>
        </thead>
        <tbody>
          {plans.map(item => (
            <tr key={item.id || item.sequence}>
              <td style={{padding:'12px 16px',borderBottom:'1px solid #e5e7eb'}}>{item.sequence}</td>
              <td style={{padding:'12px 16px',borderBottom:'1px solid #e5e7eb'}}>{item.audience}</td>
              <td style={{padding:'12px 16px',borderBottom:'1px solid #e5e7eb'}}>{item.content}</td>
              <td style={{padding:'12px 16px',borderBottom:'1px solid #e5e7eb'}}>{item.method}</td>
              <td style={{padding:'12px 16px',borderBottom:'1px solid #e5e7eb'}}>{item.time}</td>
              <td style={{padding:'12px 16px',borderBottom:'1px solid #e5e7eb'}}>{item.hours}</td>
              <td style={{padding:'12px 16px',borderBottom:'1px solid #e5e7eb'}}>{item.participants}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// --- 主页面组件 ---

const EducationTrainingPage = ({ mode = 'dashboard' }) => {
  const isLedgerMode = mode === 'ledger';
  const defaultTab = isLedgerMode ? 'ledger' : 'flow';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const { addToast } = useToasts();

  const [entrustStats, setEntrustStats] = useState({
    recordCount: 0,
    participantCount: 0,
    unitCount: 0,
    latestDate: null
  });

  const refreshEntrustStats = useCallback(() => {
    if (typeof window === 'undefined' || !window.localStorage) {
      setEntrustStats({ recordCount: 0, participantCount: 0, unitCount: 0, latestDate: null });
      return;
    }
    try {
      const raw = window.localStorage.getItem(TRAINING_ENTRUST_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) {
        setEntrustStats({ recordCount: 0, participantCount: 0, unitCount: 0, latestDate: null });
        return;
      }

      let participantCount = 0;
      let latestTimestamp = null;
      const unitSet = new Set();

      parsed.forEach((record) => {
        if (!record || typeof record !== 'object') return;
        if (record.entrustUnit && String(record.entrustUnit).trim()) {
          unitSet.add(String(record.entrustUnit).trim());
        }
        if (record.entrustDate) {
          const ts = Date.parse(record.entrustDate);
          if (!Number.isNaN(ts) && (latestTimestamp === null || ts > latestTimestamp)) {
            latestTimestamp = ts;
          }
        }
        if (Array.isArray(record.rows)) {
          record.rows.forEach((row) => {
            if (!row) return;
            if (row.name && String(row.name).trim()) {
              participantCount += 1;
            }
          });
        }
      });

      setEntrustStats({
        recordCount: parsed.length,
        participantCount,
        unitCount: unitSet.size,
        latestDate: latestTimestamp ? new Date(latestTimestamp).toISOString().slice(0, 10) : null
      });
    } catch (error) {
      console.warn('[education-training] parse training entrust stats failed:', error);
      setEntrustStats({ recordCount: 0, participantCount: 0, unitCount: 0, latestDate: null });
    }
  }, []);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const LEDGER_TEMPLATE_HEADERS = useMemo(() => ([
    '序号',
    '姓名',
    '性别',
    '部门',
    '厂、区域',
    '岗位',
    '文化程度',
    '岗前项目部教育时间',
    '授课人',
    '成绩',
    '公司级教育时间',
    '授课人',
    '成绩',
    '厂级安全教育时间',
    '授课人',
    '成绩',
    '岗前班组级教育时间',
    '授课人',
    '成绩'
  ]), []);

  // Modal 和表单状态
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null); // 用于区分新增和编辑

  // 台账 Modal 状态
  const [isLedgerImportModalOpen, setIsLedgerImportModalOpen] = useState(false);
  const [ledgerImportFile, setLedgerImportFile] = useState(null);
  const [isLedgerImporting, setIsLedgerImporting] = useState(false);
  const [ledgerImportSummary, setLedgerImportSummary] = useState(null);
  const [ledgerImportError, setLedgerImportError] = useState(null);
  const [ledgerImportInputKey, setLedgerImportInputKey] = useState(0);

  // 培训台账的状态
  const [safetyLedgers, setSafetyLedgers] = useState([]);
  const [ledgerSummaryFilter, setLedgerSummaryFilter] = useState(null);
  const [ledgerSearchFilters, setLedgerSearchFilters] = useState(() => ({ ...DEFAULT_LEDGER_FILTERS }));
  const [isSafetyLedgerModalOpen, setIsSafetyLedgerModalOpen] = useState(false);
  const [safetyLedgerModalMode, setSafetyLedgerModalMode] = useState('edit');
  const [activeSafetyLedger, setActiveSafetyLedger] = useState(null);
  const [isSafetyLedgerSubmitting, setIsSafetyLedgerSubmitting] = useState(false);
  const [missingSafetyLedgerEmployees, setMissingSafetyLedgerEmployees] = useState([]);
  const [isLedgerLoading, setIsLedgerLoading] = useState(false);

  // 特种设备人员的状态
  const [specialOperators, setSpecialOperators] = useState([]);
  const [isSpecialEquipLoading, setIsSpecialEquipLoading] = useState(false);
  const [isSpecialModalOpen, setIsSpecialModalOpen] = useState(false);
  const [editingSpecialOperator, setEditingSpecialOperator] = useState(null);
  const [specialOperatorEmployees, setSpecialOperatorEmployees] = useState([]);
  const [isLoadingSpecialEmployees, setIsLoadingSpecialEmployees] = useState(false);
  const [isSpecialSubmitting, setIsSpecialSubmitting] = useState(false);
  const [pdfPreview, setPdfPreview] = useState({ isOpen: false, url: '', name: '' });

  // 安全管理人员的状态
  const [safetyManagers, setSafetyManagers] = useState([]);
  const [isSafetyManagerLoading, setIsSafetyManagerLoading] = useState(false);
  const [isSafetyModalOpen, setIsSafetyModalOpen] = useState(false);
  const [editingSafetyManager, setEditingSafetyManager] = useState(null);
  const [safetyManagerEmployees, setSafetyManagerEmployees] = useState([]);
  const [isLoadingSafetyEmployees, setIsLoadingSafetyEmployees] = useState(false);
  const [isSafetySubmitting, setIsSafetySubmitting] = useState(false);

  // 新增计划状态
  const [plans, setPlans] = useState([]);

  const handleLedgerSearchInputChange = useCallback((event) => {
    const { name, value } = event.target;
    if (!name) return;
    setLedgerSearchFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const resetLedgerSearchFilters = useCallback(() => {
    setLedgerSearchFilters(() => ({ ...DEFAULT_LEDGER_FILTERS }));
  }, []);

  const specialOperatorStats = useMemo(() => {
    if (!Array.isArray(specialOperators) || specialOperators.length === 0) {
      return { total: 0, expiringSoon: 0, expired: 0 };
    }
    const now = new Date();
    let expiringSoon = 0;
    let expired = 0;
    specialOperators.forEach(op => {
      if (!op.expiry_date) return;
      const expiry = new Date(op.expiry_date);
      if (Number.isNaN(expiry.getTime())) return;
      const daysDiff = Math.round((expiry - now) / (1000 * 60 * 60 * 24));
      if (daysDiff < 0) expired += 1;
      else if (daysDiff <= 60) expiringSoon += 1;
    });
    return { total: specialOperators.length, expiringSoon, expired };
  }, [specialOperators]);

  const safetyManagerStats = useMemo(() => {
    if (!Array.isArray(safetyManagers) || safetyManagers.length === 0) {
      return { total: 0, expiringSoon: 0, expired: 0 };
    }
    const now = new Date();
    let expiringSoon = 0;
    let expired = 0;
    safetyManagers.forEach(item => {
      if (!item.expiry_date) return;
      const expiry = new Date(item.expiry_date);
      if (Number.isNaN(expiry.getTime())) return;
      const daysDiff = Math.round((expiry - now) / (1000 * 60 * 60 * 24));
      if (daysDiff < 0) expired += 1;
      else if (daysDiff <= 60) expiringSoon += 1;
    });
    return { total: safetyManagers.length, expiringSoon, expired };
  }, [safetyManagers]);

  const ledgerFilterOptions = useMemo(() => {
    if (!Array.isArray(safetyLedgers) || safetyLedgers.length === 0) {
      return {
        departments: [],
        sections: [],
        includeEmptyDepartment: false,
        includeEmptySection: false
      };
    }

    const departments = new Set();
    const sections = new Set();
    let includeEmptyDepartment = false;
    let includeEmptySection = false;

    safetyLedgers.forEach((item) => {
      const dept = item?.department_name;
      if (dept && String(dept).trim()) {
        departments.add(String(dept).trim());
      } else {
        includeEmptyDepartment = true;
      }

      const section = item?.plant_area;
      if (section && String(section).trim()) {
        sections.add(String(section).trim());
      } else {
        includeEmptySection = true;
      }
    });

    const sortLocale = (a, b) => a.localeCompare(b, 'zh-Hans-CN');

    return {
      departments: Array.from(departments).sort(sortLocale),
      sections: Array.from(sections).sort(sortLocale),
      includeEmptyDepartment,
      includeEmptySection
    };
  }, [safetyLedgers]);

  const hasActiveLedgerSearch = useMemo(() => {
    const { name, department, section, startDate, endDate } = ledgerSearchFilters;
    return Boolean((name && name.trim()) || department || section || startDate || endDate);
  }, [ledgerSearchFilters]);

  const safetyLedgerSummary = useMemo(() => {
    if (!Array.isArray(safetyLedgers) || safetyLedgers.length === 0) {
      return null;
    }

    const total = safetyLedgers.length;
    const uniqueEmployeeIds = new Set();
    safetyLedgers.forEach((item) => {
      if (item.employee_id) {
        uniqueEmployeeIds.add(`id:${item.employee_id}`);
      } else if (item.employee_name) {
        uniqueEmployeeIds.add(`name:${item.employee_name}`);
      }
    });
    const uniqueEmployees = uniqueEmployeeIds.size || total;

    const stageStats = STAGE_CONFIG.map((stage) => {
      let completed = 0;
      let latestTimestamp = null;
      safetyLedgers.forEach((item) => {
        const timeValue = item[stage.timeField];
        if (timeValue) {
          completed += 1;
          const ts = Date.parse(timeValue);
          if (!Number.isNaN(ts) && (latestTimestamp === null || ts > latestTimestamp)) {
            latestTimestamp = ts;
          }
        }
      });
      const pending = total - completed;
      return {
        ...stage,
        completed,
        pending,
        completionRate: total ? completed / total : 0,
        latestDate: latestTimestamp ? new Date(latestTimestamp).toISOString().slice(0, 10) : null
      };
    });

    const departmentMap = new Map();
    safetyLedgers.forEach((item) => {
      const dept = item.department_name || '未填写部门';
      departmentMap.set(dept, (departmentMap.get(dept) || 0) + 1);
    });

    const departmentStats = Array.from(departmentMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({
        name,
        count,
        ratio: total ? count / total : 0
      }));

    return { total, uniqueEmployees, stageStats, departmentStats };
  }, [safetyLedgers]);

  const filteredSafetyLedgers = useMemo(() => {
    let result = safetyLedgers;

    if (ledgerSummaryFilter) {
      result = result.filter((item) => {
        if (ledgerSummaryFilter.type === 'stage') {
          const stage = STAGE_CONFIG.find((cfg) => cfg.key === ledgerSummaryFilter.value);
          if (!stage) return true;
          return Boolean(item[stage.timeField]);
        }
        if (ledgerSummaryFilter.type === 'department') {
          const dept = item.department_name || '未填写部门';
          return dept === ledgerSummaryFilter.value;
        }
        return true;
      });
    }

    const { name, department, section, startDate, endDate } = ledgerSearchFilters;
    const normalizedName = name ? name.trim().toLowerCase() : '';
    const hasNameFilter = normalizedName.length > 0;
    const hasDepartmentFilter = Boolean(department);
    const hasSectionFilter = Boolean(section);
    const hasStartDate = Boolean(startDate);
    const hasEndDate = Boolean(endDate);

    if (!hasNameFilter && !hasDepartmentFilter && !hasSectionFilter && !hasStartDate && !hasEndDate) {
      return result;
    }

    const startTs = hasStartDate ? (() => {
      const start = new Date(startDate);
      if (Number.isNaN(start.getTime())) return null;
      return start.setHours(0, 0, 0, 0);
    })() : null;

    const endTs = hasEndDate ? (() => {
      const end = new Date(endDate);
      if (Number.isNaN(end.getTime())) return null;
      return end.setHours(23, 59, 59, 999);
    })() : null;

    return result.filter((item) => {
      if (hasNameFilter) {
        const baseName = (item.employee_name || item.employee_name_official || '').toLowerCase();
        if (!baseName.includes(normalizedName)) {
          return false;
        }
      }

      if (hasDepartmentFilter) {
        const deptValue = item.department_name && String(item.department_name).trim()
          ? String(item.department_name).trim()
          : '';
        if (department === LEDGER_EMPTY_OPTION_VALUE) {
          if (deptValue) return false;
        } else if (deptValue !== department) {
          return false;
        }
      }

      if (hasSectionFilter) {
        const sectionValue = item.plant_area && String(item.plant_area).trim()
          ? String(item.plant_area).trim()
          : '';
        if (section === LEDGER_EMPTY_OPTION_VALUE) {
          if (sectionValue) return false;
        } else if (sectionValue !== section) {
          return false;
        }
      }

      if (startTs !== null || endTs !== null) {
        const rawDates = [];
        STAGE_CONFIG.forEach((stage) => {
          const value = item[stage.timeField];
          if (value) rawDates.push(value);
        });
        if (item.training_date) {
          rawDates.push(item.training_date);
        }

        const timestamps = rawDates
          .map((raw) => {
            if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
              return raw.getTime();
            }
            const parsed = Date.parse(raw);
            return Number.isNaN(parsed) ? null : parsed;
          })
          .filter((ts) => ts !== null);

        if (!timestamps.length) {
          return false;
        }

        const matchesRange = timestamps.some((ts) => {
          if (startTs !== null && ts < startTs) return false;
          if (endTs !== null && ts > endTs) return false;
          return true;
        });

        if (!matchesRange) {
          return false;
        }
      }

      return true;
    });
  }, [ledgerSearchFilters, ledgerSummaryFilter, safetyLedgers]);

  const handleSummaryFilterToggle = useCallback((nextFilter) => {
    if (!nextFilter) {
      setLedgerSummaryFilter(null);
      return;
    }
    setLedgerSummaryFilter((prev) => (
      prev && prev.type === nextFilter.type && prev.value === nextFilter.value
        ? null
        : nextFilter
    ));
  }, []);

  const clearSummaryFilter = useCallback(() => setLedgerSummaryFilter(null), []);

  // --- CRUD 操作 ---
  const handleOpenModal = (plan = null) => {
    setEditingPlan(plan);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPlan(null);
  };

  const handlePlanSubmit = async (formData) => {
    try {
      if (editingPlan) {
        // 更新逻辑 (待实现)
        // await http.put(`/education-training/annual-plans/${editingPlan.id}`, formData);
        addToast('更新成功！(模拟)', 'success');
      } else {
        // 新增逻辑
        await http.post('/education-training/annual-plans', formData);
        addToast('年度计划已成功创建！', 'success');
      }
      handleCloseModal();
    } catch (error) {
      const errorMsg = error.response?.data?.error || '操作失败，请检查数据或联系管理员。';
      addToast(errorMsg, 'error');
    }
  };

  const handleOpenLedgerImportModal = () => {
    setIsLedgerImportModalOpen(true);
    setLedgerImportFile(null);
    setLedgerImportSummary(null);
    setLedgerImportError(null);
    setLedgerImportInputKey((key) => key + 1);
  };

  const handleCloseLedgerImportModal = () => {
    setIsLedgerImportModalOpen(false);
    setLedgerImportFile(null);
    setLedgerImportSummary(null);
    setLedgerImportError(null);
    setIsLedgerImporting(false);
    setLedgerImportInputKey((key) => key + 1);
  };

  const handleLedgerImportFileChange = (event) => {
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    setLedgerImportFile(file);
    setLedgerImportSummary(null);
    setLedgerImportError(null);
  };

  const handleExportSafetyLedgers = useCallback(async () => {
    try {
      const response = await http.get('/education-training/ledgers/export', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `安全培训台账-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      addToast('导出完成，请检查下载的 Excel 文件。', 'success');
    } catch (error) {
      const message = error.response?.data?.message || '导出失败，请稍后重试。';
      addToast(message, 'error');
      console.error('Export safety ledgers error:', error);
    }
  }, [addToast]);

  const handleLedgerTemplateDownload = () => {
    try {
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet([LEDGER_TEMPLATE_HEADERS]);
      XLSX.utils.book_append_sheet(workbook, worksheet, '导入模板');
      XLSX.writeFile(workbook, '安全培训台账批量导入模板.xlsx');
      addToast('模板已生成，请在 Excel 中录入数据后再导入。', 'success');
    } catch (err) {
      console.error('download ledger template error:', err);
      addToast('模板生成失败，请稍后重试。', 'error');
    }
  };

  const handleLedgerImportSubmit = async (event) => {
    event.preventDefault();
    if (!ledgerImportFile) {
      addToast('请选择 Excel 文件后再导入。', 'warning');
      return;
    }

    setIsLedgerImporting(true);
    setLedgerImportSummary(null);
    setLedgerImportError(null);

    try {
      const formData = new FormData();
      formData.append('file', ledgerImportFile);

      const response = await http.post('/education-training/ledgers/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setLedgerImportSummary(response.data);
      addToast('导入完成，已同步最新台账。', 'success');
      await fetchLedgers();
      setLedgerImportFile(null);
      setLedgerImportInputKey((key) => key + 1);
    } catch (err) {
      const message = err.response?.data?.error || err.message || '导入失败，请稍后重试。';
      setLedgerImportError({
        message,
        details: err.response?.data?.details,
        summary: err.response?.data?.summary
      });
      addToast(message, 'error');
    } finally {
      setIsLedgerImporting(false);
    }
  };

  const handleOpenSafetyLedgerCreate = () => {
    setSafetyLedgerModalMode('create');
    setActiveSafetyLedger({});
    setIsSafetyLedgerModalOpen(true);
  };

  const handleOpenSafetyLedgerEdit = (record) => {
    if (!record) return;
    setSafetyLedgerModalMode('edit');
    setActiveSafetyLedger(record);
    setIsSafetyLedgerModalOpen(true);
  };

  const handleCloseSafetyLedgerModal = () => {
    setIsSafetyLedgerModalOpen(false);
    setActiveSafetyLedger(null);
    setSafetyLedgerModalMode('edit');
  };

  const handleSafetyLedgerSubmit = async (formValues) => {
    setIsSafetyLedgerSubmitting(true);
    try {
      const seqValue = Number(formValues.sequence_number);
      const payload = {
        sequence_number: Number.isFinite(seqValue) ? seqValue : null,
        employee_name: formValues.employee_name ? formValues.employee_name.trim() : null,
        import_batch: formValues.import_batch ? formValues.import_batch.trim() : null,
        gender: formValues.gender || null,
        department_name: formValues.department_name || null,
        plant_area: formValues.plant_area || null,
        job_title: formValues.job_title || null,
        education_level: formValues.education_level || null
      };

      const employeeIdValue = Number(formValues.employee_id);
      payload.employee_id = Number.isFinite(employeeIdValue) && employeeIdValue > 0 ? employeeIdValue : null;

      if (!payload.employee_name && !payload.employee_id) {
        addToast('请至少填写员工姓名或关联员工编号。', 'warning');
        return;
      }

      STAGE_CONFIG.forEach((stage) => {
        payload[stage.timeField] = formValues[stage.timeField] || null;
        payload[stage.trainerField] = formValues[stage.trainerField] || null;
        payload[stage.scoreField] = formValues[stage.scoreField] || null;
      });

      if (safetyLedgerModalMode === 'edit' && activeSafetyLedger) {
        await http.put(`/education-training/ledgers/${activeSafetyLedger.rawId}`, payload);
        addToast('台账记录已更新！', 'success');
      } else {
        await http.post('/education-training/ledgers', payload);
        addToast('台账记录已保存。', 'success');
      }

      handleCloseSafetyLedgerModal();
      await fetchLedgers();
    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || '更新失败，请稍后再试。';
      addToast(message, 'error');
    } finally {
      setIsSafetyLedgerSubmitting(false);
    }
  };

  const handleDeleteSafetyLedger = async (record) => {
    if (!record) return;
    if (!window.confirm(`确定要删除【${record.employee_name || '该员工'}】的台账记录吗？`)) {
      return;
    }
    try {
      await http.delete(`/education-training/ledgers/${record.rawId}`);
      addToast('台账记录已删除。', 'success');
      await fetchLedgers();
    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || '删除失败，请稍后再试。';
      addToast(message, 'error');
    }
  };

  // 获取培训台账列表
  const fetchLedgers = useCallback(async () => {
    setIsLedgerLoading(true);
    try {
      const [safetyResponse, missingResponse] = await Promise.all([
        http.get('/education-training/ledgers').catch((err) => {
          console.warn('[education-training] 获取安全培训台账失败:', err);
          return { data: [] };
        }),
        http.get('/education-training/ledgers/missing-this-year').catch((err) => {
          console.warn('[education-training] 获取未建台账员工失败:', err);
          return { data: [] };
        })
      ]);

      const safetyList = Array.isArray(safetyResponse.data) ? safetyResponse.data.map((item) => ({
        ...item,
        rawId: item.id,
        id: `safety-${item.id}`,
        source: 'safety'
      })) : [];

      setSafetyLedgers(safetyList);
      setMissingSafetyLedgerEmployees(Array.isArray(missingResponse.data) ? missingResponse.data : []);
    } catch (error) {
      addToast('获取培训台账失败，请稍后重试。', 'error');
      console.error('Fetch ledgers error:', error);
      setMissingSafetyLedgerEmployees([]);
    } finally {
      setIsLedgerLoading(false);
    }
  }, [addToast]);

  // 获取特种设备人员列表
  const fetchSpecialOperators = useCallback(async () => {
    setIsSpecialEquipLoading(true);
    try {
      const response = await http.get('/education-training/special-operators');
      setSpecialOperators(response.data);
    } catch (error) {
      addToast('获取特种设备人员列表失败。', 'error');
      console.error("Fetch special operators error:", error);
    } finally {
      setIsSpecialEquipLoading(false);
    }
  }, [addToast]);

  const fetchSpecialOperatorEmployees = useCallback(async () => {
    setIsLoadingSpecialEmployees(true);
    try {
      const response = await http.get('/education-training/special-operators/employees');
      let list = Array.isArray(response.data) ? response.data : [];
      const currentEmpId = editingSpecialOperator?.employee_ref_id || editingSpecialOperator?.employee_id;
      if (editingSpecialOperator && currentEmpId) {
        const exists = list.some(emp => String(emp.id) === String(currentEmpId));
        if (!exists) {
          list = [
            ...list,
            {
              id: currentEmpId,
              name: editingSpecialOperator.employee_name || '当前员工',
              department: editingSpecialOperator.department || '',
              position: editingSpecialOperator.position || ''
            }
          ];
        }
      }
      setSpecialOperatorEmployees(list);
    } catch (error) {
      addToast('获取员工信息失败。', 'error');
      console.error('Fetch special operator employees error:', error);
    } finally {
      setIsLoadingSpecialEmployees(false);
    }
  }, [addToast, editingSpecialOperator]);

  const fetchSafetyManagers = useCallback(async () => {
    setIsSafetyManagerLoading(true);
    try {
      const response = await http.get('/education-training/safety-managers');
      setSafetyManagers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      addToast('获取安全管理人员持证台账失败。', 'error');
      console.error('Fetch safety managers error:', error);
    } finally {
      setIsSafetyManagerLoading(false);
    }
  }, [addToast]);

  const fetchSafetyManagerEmployees = useCallback(async () => {
    setIsLoadingSafetyEmployees(true);
    try {
      const response = await http.get('/education-training/safety-managers/employees');
      let list = Array.isArray(response.data) ? response.data : [];
      const currentEmpId = editingSafetyManager?.employee_ref_id || editingSafetyManager?.employee_id;
      if (editingSafetyManager && currentEmpId) {
        const exists = list.some(emp => String(emp.id) === String(currentEmpId));
        if (!exists) {
          list = [
            ...list,
            {
              id: currentEmpId,
              name: editingSafetyManager.employee_name || '当前员工',
              department: editingSafetyManager.department || '',
              position: editingSafetyManager.position || ''
            }
          ];
        }
      }
      setSafetyManagerEmployees(list);
    } catch (error) {
      addToast('获取安全管理人员员工信息失败。', 'error');
      console.error('Fetch safety manager employees error:', error);
    } finally {
      setIsLoadingSafetyEmployees(false);
    }
  }, [addToast, editingSafetyManager]);

  const handleRefreshFlowData = useCallback(() => {
    refreshEntrustStats();
    fetchLedgers();
  }, [refreshEntrustStats, fetchLedgers]);

  const handleOpenSpecialOperatorModal = (operator = null) => {
    setEditingSpecialOperator(operator);
    setIsSpecialModalOpen(true);
  };

  const handleCloseSpecialOperatorModal = () => {
    setEditingSpecialOperator(null);
    setIsSpecialModalOpen(false);
    setIsSpecialSubmitting(false);
  };

  const handleOpenSafetyManagerModal = (record = null) => {
    setEditingSafetyManager(record);
    setIsSafetyModalOpen(true);
  };

  const handleCloseSafetyManagerModal = () => {
    setEditingSafetyManager(null);
    setIsSafetyModalOpen(false);
    setIsSafetySubmitting(false);
  };

  const handleSpecialOperatorSubmit = async (formValues) => {
    if (isSpecialSubmitting) return;
    const { certificateFile, removeCertificate, ...rest } = formValues;
    const payload = new FormData();
    Object.entries(rest).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        payload.append(key, '');
      } else {
        payload.append(key, value);
      }
    });
    if (certificateFile) {
      payload.append('certificate_pdf', certificateFile);
    }
    if (removeCertificate) {
      payload.append('remove_certificate', 'true');
    }

    setIsSpecialSubmitting(true);
    try {
      if (editingSpecialOperator) {
        await http.put(`/education-training/special-operators/${editingSpecialOperator.id}`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        addToast('证件信息已更新！', 'success');
      } else {
        await http.post('/education-training/special-operators', payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        addToast('已登记特种设备作业人员！', 'success');
      }
      handleCloseSpecialOperatorModal();
      fetchSpecialOperators();
    } catch (error) {
      const message = error.response?.data?.message || '保存失败，请稍后重试。';
      addToast(message, 'error');
      console.error('Submit special operator error:', error);
    } finally {
      setIsSpecialSubmitting(false);
    }
  };

  const handleSafetyManagerSubmit = async (formValues) => {
    if (isSafetySubmitting) return;
    const { certificateFile, removeCertificate, ...rest } = formValues;
    const payload = new FormData();
    Object.entries(rest).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        payload.append(key, '');
      } else {
        payload.append(key, value);
      }
    });
    if (certificateFile) {
      payload.append('certificate_pdf', certificateFile);
    }
    if (removeCertificate) {
      payload.append('remove_certificate', 'true');
    }

    setIsSafetySubmitting(true);
    try {
      if (editingSafetyManager) {
        await http.put(`/education-training/safety-managers/${editingSafetyManager.id}`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        addToast('安全管理人员证件已更新！', 'success');
      } else {
        await http.post('/education-training/safety-managers', payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        addToast('已登记安全管理人员持证信息！', 'success');
      }
      handleCloseSafetyManagerModal();
      fetchSafetyManagers();
    } catch (error) {
      const message = error.response?.data?.message || '保存失败，请稍后重试。';
      addToast(message, 'error');
      console.error('Submit safety manager error:', error);
    } finally {
      setIsSafetySubmitting(false);
    }
  };

  const handleDeleteSpecialOperator = async (id) => {
    if (!window.confirm('确定删除该持证人员记录？')) return;
    try {
      await http.delete(`/education-training/special-operators/${id}`);
      addToast('已删除该记录。', 'success');
      fetchSpecialOperators();
    } catch (error) {
      const message = error.response?.data?.message || '删除失败，请稍后重试。';
      addToast(message, 'error');
      console.error('Delete special operator error:', error);
    }
  };

  const handleDeleteSafetyManager = async (id) => {
    if (!window.confirm('确定删除该安全管理人员记录？')) return;
    try {
      await http.delete(`/education-training/safety-managers/${id}`);
      addToast('已删除该安全管理人员记录。', 'success');
      fetchSafetyManagers();
    } catch (error) {
      const message = error.response?.data?.message || '删除失败，请稍后重试。';
      addToast(message, 'error');
      console.error('Delete safety manager error:', error);
    }
  };

  const handlePreviewCertificate = (operator) => {
    if (!operator?.certificate_url) {
      addToast('暂无证件 PDF 可预览。', 'info');
      return;
    }
    setPdfPreview({
      isOpen: true,
      url: operator.certificate_url,
      name: operator.certificate_name || `${operator.employee_name || '证件'}.pdf`
    });
  };

  const handleClosePdfPreview = () => {
    setPdfPreview({ isOpen: false, url: '', name: '' });
  };

  const handleExportSpecialOperators = async () => {
    try {
      const response = await http.get('/education-training/special-operators/export', {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `特种设备作业人员台账_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      addToast('监管报表导出成功！', 'success');
    } catch (error) {
      const message = error.response?.data?.message || '导出失败，请稍后重试。';
      addToast(message, 'error');
      console.error('Export special operators error:', error);
    }
  };

  const handleExportSafetyManagers = async () => {
    try {
      const response = await http.get('/education-training/safety-managers/export', {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `安全管理人员持证台账_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      addToast('安全管理人员台账导出成功！', 'success');
    } catch (error) {
      const message = error.response?.data?.message || '导出失败，请稍后重试。';
      addToast(message, 'error');
      console.error('Export safety managers error:', error);
    }
  };

  // 获取培训计划
  useEffect(() => {
    http.get('/training_plans')
      .then((res) => {
        const data = res?.data;
        setPlans(Array.isArray(data) ? data : []);
      })
      .catch(() => setPlans([]));
  }, []);

  // 在对应标签页激活时加载数据
  useEffect(() => {
    if (activeTab === 'ledger' || (!isLedgerMode && activeTab === 'flow')) {
      fetchLedgers();
    } else if (activeTab === 'specialEquip') {
      fetchSpecialOperators();
    } else if (activeTab === 'safetyManagers') {
      fetchSafetyManagers();
    }
  }, [activeTab, isLedgerMode, fetchLedgers, fetchSpecialOperators, fetchSafetyManagers]);

  useEffect(() => {
    if (!isSpecialModalOpen) return;
    fetchSpecialOperatorEmployees();
  }, [isSpecialModalOpen, fetchSpecialOperatorEmployees]);

  useEffect(() => {
    if (!isSafetyModalOpen) return;
    fetchSafetyManagerEmployees();
  }, [isSafetyModalOpen, fetchSafetyManagerEmployees]);

  useEffect(() => {
    if (isLedgerMode) return;
    if (activeTab !== 'flow') return;
    refreshEntrustStats();
  }, [isLedgerMode, activeTab, refreshEntrustStats]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleStorage = (event) => {
      if (event?.key === TRAINING_ENTRUST_STORAGE_KEY) {
        refreshEntrustStats();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [refreshEntrustStats]);


  // 标签页根据模式动态呈现
  const tabs = useMemo(() => (
    isLedgerMode
      ? [
          { id: 'ledger', label: '安全培训台账' },
          { id: 'trainingPlans', label: '培训计划' },
          { id: 'onJobLedger', label: '岗中教育培训台账' },
          { id: 'specialEquip', label: '特种设备作业人员台账及证件' },
          { id: 'safetyManagers', label: '安全管理人员持证台账' }
        ]
      : [
          { id: 'flow', label: '流程看板' },
          { id: 'annual', label: '年度安全教育培训计划' }
        ]
  ), [isLedgerMode]);

  // 通用样式
  const pageTitle = isLedgerMode ? '培训台账' : '教育培训看板';
  const containerStyle = { fontFamily: "'Segoe UI','Roboto','Helvetica Neue',Arial,sans-serif" };
  const headerStyle = { marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' };
  const tabContainerStyle = { display: 'flex', gap: '0.25rem', borderBottom: '2px solid var(--border-color)', marginBottom: '1.25rem', flexWrap: 'wrap' };
  const tabButtonStyle = {
    padding: '0.75rem 1.1rem', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14,
    color: 'var(--text-secondary)', borderBottom: '3px solid transparent', borderRadius: '6px 6px 0 0',
    fontWeight: 500, transition: 'color .2s, background .2s, border-color .2s'
  };
  const activeTabButtonStyle = { ...tabButtonStyle, color: 'var(--brand-primary)', background: 'var(--bg-secondary)', borderBottom: '3px solid var(--brand-primary)', fontWeight: 600 };
  const contentWrapperStyle = { background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 12, padding: '1.25rem 1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', minHeight: 260 };
  const sectionTitleStyle = { fontSize: '1.1rem', fontWeight: 600, margin: '0 0 .75rem', color: 'var(--text-primary)' };
  const hintStyle = { fontSize: 12, color: 'var(--text-secondary)', marginTop: '.75rem', lineHeight: 1.5 };
  const actionsBarStyle = { display: 'flex', gap: '.75rem', marginBottom: '1rem', flexWrap: 'wrap' };
  const btn = (variant='primary') => ({
    padding: '.55rem 1rem', fontSize: 13, borderRadius: 6, cursor: 'pointer', fontWeight: 500, border: '1px solid',
    background: variant==='primary'? 'var(--brand-primary)' : 'var(--bg-tertiary)',
    color: variant==='primary'? '#fff':'var(--text-primary)',
    borderColor: variant==='primary'? 'var(--brand-primary)':'var(--border-color)',
    transition: 'background .2s, color .2s, border-color .2s'
  });
  const flowGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14, marginBottom: 20 };
  const flowCardBaseStyle = {
    position: 'relative',
    background: '#fff',
    borderRadius: 12,
    border: '1px solid var(--border-color)',
    padding: '16px 18px',
    boxShadow: '0 8px 24px rgba(15,23,42,0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minHeight: 150
  };
  const buildFlowCardStyle = (accent) => ({
    ...flowCardBaseStyle,
    borderTop: `4px solid ${accent}`
  });
  const flowCardTitleStyle = { fontSize: 12, color: '#475569', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase' };
  const flowCardMetricStyle = { fontSize: 28, fontWeight: 700, color: '#0f172a' };
  const flowCardMetaStyle = { fontSize: 12, color: '#475569' };
  const flowCardMetaSubtleStyle = { fontSize: 12, color: '#94a3b8' };
  const flowCardFooterStyle = { marginTop: 'auto', fontSize: 12, color: '#64748b' };
  const flowProcessContainerStyle = { background: '#fff', borderRadius: 16, border: '1px solid var(--border-color)', padding: '18px 22px', boxShadow: '0 12px 28px rgba(15,23,42,0.06)', marginBottom: 24 };
  const flowProcessHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 };
  const flowProcessTitleStyle = { fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 };
  const flowProcessSubtitleStyle = { fontSize: 12, color: '#475569', maxWidth: 520, lineHeight: 1.6 };
  const flowStepListStyle = { display: 'flex', flexWrap: 'wrap', gap: 16 };
  const buildFlowStepStyle = (bg, accent, isLast) => ({
    flex: '1 1 200px',
    minWidth: 180,
    minHeight: 160,
    background: bg,
    color: '#0f172a',
    borderRadius: 16,
    clipPath: isLast ? 'polygon(0 0,100% 0,100% 100%,0 100%)' : 'polygon(0 0,calc(100% - 26px) 0,100% 50%,calc(100% - 26px) 100%,0 100%)',
    padding: '18px 20px',
    boxShadow: '0 12px 30px rgba(15,23,42,0.08)',
    border: `1px solid ${accent}33`,
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  });
  const flowStepIndexStyle = (accent) => ({
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: accent,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    fontWeight: 700,
    boxShadow: '0 4px 12px rgba(15,23,42,0.16)'
  });
  const flowStepTitleStyle = { fontSize: 15, fontWeight: 700, color: '#0f172a' };
  const flowStepStatusStyle = (accent) => ({ fontSize: 12, fontWeight: 600, color: accent, letterSpacing: '.08em' });
  const flowStepDescStyle = { fontSize: 12, color: '#475569', lineHeight: 1.6 };
  const flowStepMetricContainerStyle = (accent) => ({
    marginTop: 'auto',
    background: '#fff',
    borderRadius: 12,
    border: `1px solid ${accent}44`,
    padding: '10px 12px',
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.6)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  });
  const flowStepMetricLabelStyle = { fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.12em' };
  const flowStepMetricValueStyle = (accent) => ({ fontSize: 20, fontWeight: 700, color: accent });
  const flowStepMetricSubStyle = { fontSize: 11, color: '#475569' };
  const summaryGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '10px', marginBottom: 12 };
  const summaryCardStyle = {
    background: '#fff',
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'var(--border-color)',
    padding: '12px 14px',
    boxShadow: '0 6px 14px rgba(15,23,42,0.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    transition: 'box-shadow .2s, transform .2s, border-color .2s'
  };
  const departmentWrapStyle = { display: 'flex', flexWrap: 'wrap', gap: 8 };
  const listCardStyle = {
    background: '#f8fafc',
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'var(--border-color)',
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    cursor: 'pointer',
    boxShadow: '0 4px 10px rgba(15,23,42,0.05)',
    transition: 'box-shadow .2s, transform .2s, border-color .2s',
    flex: '1 0 170px'
  };
  const filterInfoStyle = { display: 'flex', alignItems: 'center', gap: 10, background: '#eef2ff', borderRadius: 10, padding: '8px 12px', border: '1px solid #c7d2fe', fontSize: 12, color: '#4338ca' };
  const filterClearBtnStyle = { border: 'none', background: 'transparent', color: '#1d4ed8', cursor: 'pointer', fontSize: 12, padding: '4px 8px', borderRadius: 6 };
  const missingWrapStyle = { display: 'flex', flexWrap: 'wrap', gap: 10 };
  const missingChipStyle = {
    border: '1px dashed #94a3b8',
    borderRadius: 10,
    padding: '10px 12px',
    background: '#fff',
    fontSize: 12,
    color: '#1f2937',
    minWidth: 190,
    boxShadow: '0 4px 12px rgba(15,23,42,0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  };
  const ledgerFilterContainerStyle = { margin: '16px 0', padding: '12px 16px', borderRadius: 12, border: '1px solid var(--border-color)', background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 10 };
  const ledgerFilterGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 };
  const ledgerFilterFieldStyle = { display: 'flex', flexDirection: 'column', gap: 4 };
  const ledgerFilterLabelStyle = { fontSize: 12, color: '#64748b' };
  const ledgerFilterInputStyle = { width: '100%', padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, background: '#fff' };

  // 各分类内容渲染
  const renderFlowBoard = () => {
    const summary = safetyLedgerSummary;
    const stageStats = (summary && Array.isArray(summary.stageStats) ? summary.stageStats : STAGE_CONFIG.map((stage) => ({
      ...stage,
      completed: 0,
      pending: 0,
      completionRate: 0,
      latestDate: null
    }))).map((stat) => ({
      ...stat,
      latestDate: stat.latestDate ? formatCardDate(stat.latestDate) : null
    }));

    const totalRecords = summary?.total ?? 0;
    const uniqueEmployees = summary?.uniqueEmployees ?? 0;
    const latestLedgerTimestamp = stageStats.reduce((acc, stat) => {
      if (!stat.latestDate) return acc;
      const ts = Date.parse(stat.latestDate);
      if (Number.isNaN(ts)) return acc;
      if (acc === null || ts > acc) return ts;
      return acc;
    }, null);
    const latestLedgerDate = latestLedgerTimestamp ? new Date(latestLedgerTimestamp).toISOString().slice(0, 10) : null;
    const entrustLatestDisplay = entrustStats.latestDate ? formatCardDate(entrustStats.latestDate) : '—';
    const stageMap = stageStats.reduce((acc, stat) => ({ ...acc, [stat.key]: stat }), {});
    const projectStat = stageMap.project || {};
    const companyStat = stageMap.company || {};
    const siteStat = stageMap.site || {};
    const teamStat = stageMap.team || {};
    const tierCoverageRates = [companyStat.completionRate, siteStat.completionRate, teamStat.completionRate].filter((value) => Number.isFinite(value) && value >= 0);
    const tierCoverageAvg = tierCoverageRates.length ? tierCoverageRates.reduce((sum, value) => sum + value, 0) / tierCoverageRates.length : 0;
    const tierCompleted = (companyStat.completed || 0) + (siteStat.completed || 0) + (teamStat.completed || 0);
    const flowSteps = [
      {
        id: 'apply',
        title: '入职申请',
        status: '（同意）',
        description: '招聘审批完成，触发入职准备。',
        theme: { bg: '#e0f2ff', accent: '#2563eb' },
        metricLabel: '流程数据',
        metricValue: '待接入',
        metricSub: '与招聘系统打通后显示审批通过数量。'
      },
      {
        id: 'project',
        title: '项目部培训',
        status: '（合格）',
        description: '项目部进行岗前教育与考试，确认安全意识。',
        theme: { bg: '#dcfce7', accent: '#15803d' },
        metricLabel: '合格人数',
        metricValue: totalRecords ? `${projectStat.completed || 0} 人` : '—',
        metricSub: totalRecords ? `覆盖率 ${formatPercent(projectStat.completionRate || 0)}` : '等待台账数据'
      },
      {
        id: 'entrust',
        title: '培训委托',
        status: '',
        description: '与培训机构确认计划，生成委托函与信息表。',
        theme: { bg: '#ede9fe', accent: '#6d28d9' },
        metricLabel: '委托记录',
        metricValue: `${entrustStats.recordCount || 0} 条`,
        metricSub: entrustStats.participantCount ? `累计参培 ${entrustStats.participantCount} 人` : '等待填报'
      },
      {
        id: 'triple-stage',
        title: '三级安全教育培训',
        status: '',
        description: '公司级、厂级、班组级逐级审核学习效果并归档。',
        theme: { bg: '#fef3c7', accent: '#b45309' },
        metricLabel: '综合覆盖率',
        metricValue: tierCoverageRates.length ? formatPercent(tierCoverageAvg) : '—',
        metricSub: tierCompleted ? `完成记录 ${tierCompleted} 条` : '等待补录'
      },
      {
        id: 'onboard',
        title: '到岗',
        status: '',
        description: '员工完成培训并到岗，进入日常安全管理。',
        theme: { bg: '#fee2e2', accent: '#b91c1c' },
        metricLabel: '到岗人数',
        metricValue: totalRecords ? `${uniqueEmployees} 人` : '—',
        metricSub: latestLedgerDate ? `最近到岗：${latestLedgerDate}` : '等待台账数据'
      }
    ];

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <h2 style={sectionTitleStyle}>流程看板</h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              style={{
                ...btn('secondary'),
                minWidth: 120,
                opacity: isLedgerLoading ? 0.65 : 1,
                cursor: isLedgerLoading ? 'not-allowed' : 'pointer'
              }}
              onClick={handleRefreshFlowData}
              disabled={isLedgerLoading}
            >
              {isLedgerLoading ? '刷新中...' : '刷新数据'}
            </button>
          </div>
        </div>
        {isLedgerLoading ? (
          <div>正在汇总培训台账数据...</div>
        ) : (
          <>
            <div style={flowGridStyle}>
              {stageStats.map((stat) => (
                <div key={stat.key} style={buildFlowCardStyle(stat.color)}>
                  <div style={flowCardTitleStyle}>{stat.label}</div>
                  <div style={flowCardMetricStyle}>{stat.completed}{totalRecords ? ` / ${totalRecords}` : ''}</div>
                  <div style={flowCardMetaStyle}>覆盖率 {formatPercent(stat.completionRate)} · 待补 {stat.pending}</div>
                  <div style={flowCardFooterStyle}>最近培训：{stat.latestDate || '—'}</div>
                </div>
              ))}
              <div key="entrust" style={buildFlowCardStyle('#7c3aed')}>
                <div style={flowCardTitleStyle}>培训委托</div>
                <div style={flowCardMetricStyle}>{entrustStats.recordCount}</div>
                <div style={flowCardMetaStyle}>委托记录 · 参培人数 {entrustStats.participantCount}</div>
                <div style={flowCardMetaSubtleStyle}>涉及单位 {entrustStats.unitCount}</div>
                <div style={flowCardFooterStyle}>最近委托：{entrustLatestDisplay}</div>
              </div>
            </div>
            <div style={flowProcessContainerStyle}>
              <div style={flowProcessHeaderStyle}>
                <h3 style={flowProcessTitleStyle}>员工入职安全培训流程</h3>
                <div style={flowProcessSubtitleStyle}>参考“入职申请 → 项目部培训 → 培训委托 → 三级安全教育 → 到岗”的全流程闭环，实时查看培训执行情况。</div>
              </div>
              <div style={flowStepListStyle}>
                {flowSteps.map((step, index) => (
                  <div key={step.id} style={buildFlowStepStyle(step.theme.bg, step.theme.accent, index === flowSteps.length - 1)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={flowStepIndexStyle(step.theme.accent)}>{index + 1}</div>
                      <div>
                        <div style={flowStepTitleStyle}>{step.title}</div>
                        {step.status ? <div style={flowStepStatusStyle(step.theme.accent)}>{step.status}</div> : null}
                      </div>
                    </div>
                    <div style={flowStepDescStyle}>{step.description}</div>
                    {step.metricLabel && (
                      <div style={flowStepMetricContainerStyle(step.theme.accent)}>
                        <div style={flowStepMetricLabelStyle}>{step.metricLabel}</div>
                        <div style={flowStepMetricValueStyle(step.theme.accent)}>{step.metricValue}</div>
                        {step.metricSub ? <div style={flowStepMetricSubStyle}>{step.metricSub}</div> : null}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {summary && totalRecords > 0 ? (
              <>
                <p style={hintStyle}>
                  共 {uniqueEmployees || totalRecords} 名员工完成至少一项培训。
                  最近更新：{latestLedgerDate || '—'}；数据来源于安全培训台账与本地培训委托记录。
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {stageStats.map((stat) => (
                    <div key={`${stat.key}-progress`} style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--border-color)', padding: '12px 16px', boxShadow: '0 6px 16px rgba(15,23,42,0.05)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontWeight: 600, color: '#0f172a' }}>{stat.label}</span>
                        <span style={{ fontSize: 12, color: '#475569' }}>覆盖率 {formatPercent(stat.completionRate)}</span>
                      </div>
                      <div style={{ height: 8, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden', marginBottom: 6 }}>
                        <div style={{ width: `${Math.min(100, Math.round((stat.completionRate || 0) * 100))}%`, background: stat.color, height: '100%' }} />
                      </div>
                      <div style={flowCardMetaSubtleStyle}>已完成 {stat.completed} 人 · 待补 {stat.pending} 人</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ background: '#f8fafc', border: '1px dashed var(--border-color)', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#475569' }}>
                暂无培训台账数据，记录完成后可在此查看各流程完成情况。
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderAnnualPlan = () => (
    <div>
      <h2 style={sectionTitleStyle}>年度安全教育培训计划</h2>
      <div style={actionsBarStyle}>
        <button style={btn('primary')} onClick={() => handleOpenModal()}>新增年度计划</button>
        <button style={btn('secondary')} onClick={() => addToast('“导入计划”功能待实现。', 'info')}>导入计划</button>
        <button style={btn('secondary')} onClick={() => addToast('“导出模板”功能待实现。', 'info')}>导出模板</button>
      </div>
      {/* 培训计划列表放在新增按钮下方 */}
      <TrainingPlanList plans={plans} />
      <p style={hintStyle}>后续可增加：计划执行进度统计、完成率图表、责任人提醒。</p>
    </div>
  );

  const renderLedger = () => {
    return (
      <div>
        <h2 style={sectionTitleStyle}>安全培训台账（三级岗前 / 岗中）</h2>
        <div style={{ ...actionsBarStyle, gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <button style={btn('primary')} onClick={handleOpenSafetyLedgerCreate}>登记培训台账</button>
            <button style={btn('secondary')} onClick={handleOpenLedgerImportModal}>批量导入</button>
            <button style={btn('secondary')} onClick={handleExportSafetyLedgers}>导出Excel</button>
          </div>
        </div>
        <div style={ledgerFilterContainerStyle}>
          <div style={ledgerFilterGridStyle}>
            <div style={ledgerFilterFieldStyle}>
              <label htmlFor="ledger-filter-name" style={ledgerFilterLabelStyle}>姓名</label>
              <input
                id="ledger-filter-name"
                name="name"
                value={ledgerSearchFilters.name}
                onChange={handleLedgerSearchInputChange}
                style={ledgerFilterInputStyle}
                placeholder="输入姓名关键字"
              />
            </div>
            <div style={ledgerFilterFieldStyle}>
              <label htmlFor="ledger-filter-start" style={ledgerFilterLabelStyle}>开始日期</label>
              <input
                id="ledger-filter-start"
                type="date"
                name="startDate"
                value={ledgerSearchFilters.startDate}
                onChange={handleLedgerSearchInputChange}
                style={ledgerFilterInputStyle}
              />
            </div>
            <div style={ledgerFilterFieldStyle}>
              <label htmlFor="ledger-filter-end" style={ledgerFilterLabelStyle}>结束日期</label>
              <input
                id="ledger-filter-end"
                type="date"
                name="endDate"
                value={ledgerSearchFilters.endDate}
                onChange={handleLedgerSearchInputChange}
                style={ledgerFilterInputStyle}
              />
            </div>
            <div style={ledgerFilterFieldStyle}>
              <label htmlFor="ledger-filter-department" style={ledgerFilterLabelStyle}>部门</label>
              <select
                id="ledger-filter-department"
                name="department"
                value={ledgerSearchFilters.department}
                onChange={handleLedgerSearchInputChange}
                style={ledgerFilterInputStyle}
              >
                <option value="">全部部门</option>
                {ledgerFilterOptions.departments.map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
                {ledgerFilterOptions.includeEmptyDepartment && (
                  <option value={LEDGER_EMPTY_OPTION_VALUE}>未填写部门</option>
                )}
              </select>
            </div>
            <div style={ledgerFilterFieldStyle}>
              <label htmlFor="ledger-filter-section" style={ledgerFilterLabelStyle}>科室/区域</label>
              <select
                id="ledger-filter-section"
                name="section"
                value={ledgerSearchFilters.section}
                onChange={handleLedgerSearchInputChange}
                style={ledgerFilterInputStyle}
              >
                <option value="">全部科室</option>
                {ledgerFilterOptions.sections.map((sectionOption) => (
                  <option key={sectionOption} value={sectionOption}>{sectionOption}</option>
                ))}
                {ledgerFilterOptions.includeEmptySection && (
                  <option value={LEDGER_EMPTY_OPTION_VALUE}>未填写科室</option>
                )}
              </select>
            </div>
          </div>
        </div>
        {hasActiveLedgerSearch && (
          <div style={{ ...filterInfoStyle, marginBottom: 12 }}>
            <span>已应用检索条件</span>
            <span style={{ color: '#1d4ed8' }}>结果 {filteredSafetyLedgers.length} 条</span>
            <button type="button" style={filterClearBtnStyle} onClick={resetLedgerSearchFilters}>清除</button>
          </div>
        )}
        {isLedgerLoading ? (
          <div>正在加载数据...</div>
        ) : (
          <>
            {safetyLedgerSummary && (
              <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div style={summaryGridStyle}>
                  <div
                    style={{
                      ...summaryCardStyle,
                      background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)',
                      color: '#fff',
                      cursor: 'pointer'
                    }}
                    role="button"
                    tabIndex={0}
                    onClick={clearSummaryFilter}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        clearSummaryFilter();
                      }
                    }}
                  >
                    <div style={{ fontSize: 12, opacity: 0.85 }}>导入员工数</div>
                    <div style={{ fontSize: 26, fontWeight: 700 }}>{safetyLedgerSummary.uniqueEmployees}</div>
                    <div style={{ fontSize: 11, opacity: 0.85 }}>记录总数：{safetyLedgerSummary.total}</div>
                  </div>
                  {safetyLedgerSummary.stageStats.map((stat) => {
                    const isActive = ledgerSummaryFilter?.type === 'stage' && ledgerSummaryFilter.value === stat.key;
                    const handleCardClick = () => handleSummaryFilterToggle({ type: 'stage', value: stat.key, label: stat.label });
                    return (
                      <div
                        key={stat.key}
                        style={{
                          ...summaryCardStyle,
                          borderTop: `3px solid ${stat.color}`,
                          cursor: 'pointer',
                          borderColor: isActive ? stat.color : 'var(--border-color)',
                          boxShadow: isActive ? '0 12px 26px rgba(37,99,235,0.14)' : '0 6px 14px rgba(15,23,42,0.06)',
                          transform: isActive ? 'translateY(-2px)' : 'none'
                        }}
                        role="button"
                        tabIndex={0}
                        onClick={handleCardClick}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleCardClick();
                          }
                        }}
                      >
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>{stat.label}</div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                          <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{stat.completed}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>完成</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>覆盖率 {formatPercent(stat.completionRate)} · 待补 {stat.pending}</div>
                        {stat.latestDate && (
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>最新培训：{stat.latestDate}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {safetyLedgerSummary.departmentStats.length > 0 && (
                  <div>
                    <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>前五部门</h3>
                    <div style={departmentWrapStyle}>
                      {safetyLedgerSummary.departmentStats.map((dept) => {
                        const isActive = ledgerSummaryFilter?.type === 'department' && ledgerSummaryFilter.value === dept.name;
                        const handleDeptClick = () => handleSummaryFilterToggle({ type: 'department', value: dept.name, label: `部门：${dept.name}` });
                        return (
                          <div
                            key={dept.name}
                            style={{
                              ...listCardStyle,
                              borderColor: isActive ? '#2563eb' : 'var(--border-color)',
                              boxShadow: isActive ? '0 10px 22px rgba(37,99,235,0.14)' : listCardStyle.boxShadow,
                              transform: isActive ? 'translateY(-2px)' : 'none'
                            }}
                            role="button"
                            tabIndex={0}
                            onClick={handleDeptClick}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleDeptClick();
                              }
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--text-primary)' }}>
                              <span>{dept.name}</span>
                              <span>{dept.count} 人</span>
                            </div>
                            <div style={{ height: 5, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: Math.min(100, Math.round((dept.ratio || 0) * 100)) + '%', background: isActive ? '#1d4ed8' : '#3b82f6' }} />
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>占比 {formatPercent(dept.ratio)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div>
                  <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                    当年入职未见台账{missingSafetyLedgerEmployees.length > 0 ? `（${missingSafetyLedgerEmployees.length}）` : ''}
                  </h3>
                  {missingSafetyLedgerEmployees.length > 0 ? (
                    <div style={missingWrapStyle}>
                      {missingSafetyLedgerEmployees.map((emp) => (
                        <div key={emp.id || emp.name} style={missingChipStyle}>
                          <div style={{ fontWeight: 600 }}>{emp.name || '未命名员工'}</div>
                          <div style={{ color: '#64748b' }}>{emp.department || '未填写部门'} · {emp.position || '未填写岗位'}</div>
                          <div style={{ fontSize: 11, color: '#1d4ed8' }}>入职日期：{emp.hire_date ? String(emp.hire_date).slice(0, 10) : '—'}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#64748b', background: '#f8fafc', borderRadius: 10, padding: '8px 12px', border: '1px solid var(--border-color)' }}>
                      本年度入职员工均已登记培训台账。
                    </div>
                  )}
                </div>
              </div>
            )}
            {ledgerSummaryFilter && (
              <div style={{ ...filterInfoStyle, marginBottom: 12 }}>
                <span>当前筛选：{ledgerSummaryFilter.label}</span>
                <span style={{ color: '#1d4ed8' }}>结果 {filteredSafetyLedgers.length} 条</span>
                <button type="button" style={filterClearBtnStyle} onClick={clearSummaryFilter}>清除</button>
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <SafetyLedgerTable
                records={filteredSafetyLedgers}
                activeFilter={ledgerSummaryFilter}
                onEdit={handleOpenSafetyLedgerEdit}
                onDelete={handleDeleteSafetyLedger}
              />
            </div>
          </>
        )}
        <p style={hintStyle}>批量导入数据按模板列展示；如需修改，可通过“批量导入”覆盖或直接在“登记培训台账”弹窗中补录。</p>
      </div>
    );
  };

  const renderSpecialEquip = () => {
    const { total, expired, expiringSoon } = specialOperatorStats;
    return (
      <div>
        <h2 style={sectionTitleStyle}>特种设备作业人员台账及证件</h2>
        <div style={actionsBarStyle}>
          <button style={btn('primary')} onClick={() => handleOpenSpecialOperatorModal()}>登记持证人员</button>
          <button style={btn('secondary')} onClick={handleExportSpecialOperators}>导出监管报表</button>
        </div>
        {total > 0 && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={{ background: '#f1f5f9', borderRadius: 8, padding: '12px 16px', minWidth: 160 }}>
              <div style={{ fontSize: 12, color: '#475569' }}>持证总人数</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>{total}</div>
            </div>
            <div style={{ background: '#fef3c7', borderRadius: 8, padding: '12px 16px', minWidth: 160 }}>
              <div style={{ fontSize: 12, color: '#92400e' }}>60 天内到期</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#b45309' }}>{expiringSoon}</div>
            </div>
            <div style={{ background: '#fee2e2', borderRadius: 8, padding: '12px 16px', minWidth: 160 }}>
              <div style={{ fontSize: 12, color: '#991b1b' }}>已过期</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#b91c1c' }}>{expired}</div>
            </div>
          </div>
        )}
        {isSpecialEquipLoading ? (
          <div>正在加载数据...</div>
        ) : (
          <SpecialEquipTable
            operators={specialOperators}
            onEdit={handleOpenSpecialOperatorModal}
            onDelete={handleDeleteSpecialOperator}
            onPreview={handlePreviewCertificate}
          />
        )}
        <p style={hintStyle}>到期状态自动高亮，支持证件 PDF 在线预览与导出监管报表。</p>
      </div>
    );
  };

  const renderSafetyManagers = () => {
    const { total, expired, expiringSoon } = safetyManagerStats;
    return (
      <div>
        <h2 style={sectionTitleStyle}>安全管理人员持证台账</h2>
        <div style={actionsBarStyle}>
          <button style={btn('primary')} onClick={() => handleOpenSafetyManagerModal()}>登记安全管理人员</button>
          <button style={btn('secondary')} onClick={handleExportSafetyManagers}>导出监管报表</button>
        </div>
        {total > 0 && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={{ background: '#f1f5f9', borderRadius: 8, padding: '12px 16px', minWidth: 160 }}>
              <div style={{ fontSize: 12, color: '#475569' }}>持证总人数</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>{total}</div>
            </div>
            <div style={{ background: '#fef3c7', borderRadius: 8, padding: '12px 16px', minWidth: 160 }}>
              <div style={{ fontSize: 12, color: '#92400e' }}>60 天内到期</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#b45309' }}>{expiringSoon}</div>
            </div>
            <div style={{ background: '#fee2e2', borderRadius: 8, padding: '12px 16px', minWidth: 160 }}>
              <div style={{ fontSize: 12, color: '#991b1b' }}>已过期</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#b91c1c' }}>{expired}</div>
            </div>
          </div>
        )}
        {isSafetyManagerLoading ? (
          <div>正在加载数据...</div>
        ) : (
          <SafetyManagerTable
            records={safetyManagers}
            onEdit={handleOpenSafetyManagerModal}
            onDelete={handleDeleteSafetyManager}
            onPreview={handlePreviewCertificate}
          />
        )}
        <p style={hintStyle}>支持证件 PDF 预览与导出监管报表，可为后续安全管理复训预警做准备。</p>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'flow': return renderFlowBoard();
      case 'annual': return renderAnnualPlan();
      case 'ledger': return renderLedger();
      case 'trainingPlans': return <TrainingPlansTab addToast={addToast} />;
      case 'onJobLedger': return <OnJobTrainingLedgerTab addToast={addToast} />;
      case 'specialEquip': return renderSpecialEquip();
      case 'safetyManagers': return renderSafetyManagers();
      // case 'examSystem': 考试系统已拆出，不再在此渲染
      default: return null;
    }
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={{ fontSize: '1.85rem', fontWeight: 700, margin: 0 }}>{pageTitle}</h1>
      </header>
      <div style={tabContainerStyle}>
        {tabs.map(t => (
          <button
            key={t.id}
            style={activeTab === t.id ? activeTabButtonStyle : tabButtonStyle}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div style={contentWrapperStyle}>{renderContent()}</div>
      <WideModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal}
        title={editingPlan ? '编辑年度计划' : '新增年度计划'}
        size="wide"
      >
        <AnnualPlanForm 
          onSubmit={handlePlanSubmit}
          initialData={editingPlan}
        />
  </WideModal>
      <WideModal
        isOpen={isSafetyLedgerModalOpen}
        onClose={handleCloseSafetyLedgerModal}
        title={safetyLedgerModalMode === 'edit' ? '编辑安全培训台账' : '登记安全培训台账'}
        size="wide"
      >
        <SafetyLedgerForm
          record={activeSafetyLedger}
          mode={safetyLedgerModalMode}
          onSubmit={handleSafetyLedgerSubmit}
          onCancel={handleCloseSafetyLedgerModal}
          submitting={isSafetyLedgerSubmitting}
        />
  </WideModal>
      <WideModal
        isOpen={isLedgerImportModalOpen}
        onClose={handleCloseLedgerImportModal}
        title="安全培训台账批量导入"
        size="lg"
      >
        <form onSubmit={handleLedgerImportSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ border: '1px dashed var(--border-color)', borderRadius: 8, padding: '12px 16px', background: 'var(--bg-primary)', lineHeight: 1.6, fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>表头顺序需严格与模板一致：</div>
            <ol style={{ margin: 0, paddingLeft: '1.2rem', display: 'grid', gap: 4 }}>
              {LEDGER_TEMPLATE_HEADERS.map((header, idx) => (
                <li key={`${header}-${idx}`} style={{ color: 'var(--text-secondary)' }}>{header}</li>
              ))}
            </ol>
            <div style={{ marginTop: 10, color: 'var(--text-secondary)' }}>提示：四段“授课人 / 成绩”列依次对应【项目部 → 公司级 → 厂级 → 班组级】，请勿调整列顺序或改写列名。</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ fontSize: 13, color: 'var(--text-secondary)' }}>选择 Excel 文件（支持 .xlsx / .xls）：</label>
            <input key={ledgerImportInputKey} type="file" accept=".xlsx,.xls" onChange={handleLedgerImportFileChange} />
            {ledgerImportFile && (
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>已选择：{ledgerImportFile.name}</span>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button
              type="button"
              style={{ ...btn('secondary'), minWidth: 120 }}
              onClick={handleLedgerTemplateDownload}
            >下载模板</button>
            <button
              type="submit"
              style={{
                ...btn('primary'),
                minWidth: 120,
                opacity: isLedgerImporting ? 0.75 : 1,
                cursor: isLedgerImporting ? 'not-allowed' : 'pointer'
              }}
              disabled={isLedgerImporting}
            >{isLedgerImporting ? '导入中...' : '开始导入'}</button>
          </div>
        </form>
        {ledgerImportError && (
          <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 8, background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600 }}>导入失败</div>
            <div style={{ marginTop: 4 }}>{ledgerImportError.message}</div>
            {ledgerImportError.details && (
              <div style={{ marginTop: 8 }}>
                <div>预期表头：{ledgerImportError.details.expected}</div>
                {ledgerImportError.details.received && (
                  <div>实际表头：{ledgerImportError.details.received}</div>
                )}
              </div>
            )}
          </div>
        )}
        {ledgerImportSummary && ledgerImportSummary.summary && (
          <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-primary)', fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>导入结果</div>
            <div style={{ marginTop: 6, color: 'var(--text-secondary)' }}>导入批次：{ledgerImportSummary.import_batch || '—'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 8, marginTop: 8 }}>
              <div>总数据行：{ledgerImportSummary.summary.total}</div>
              <div>成功写入：{ledgerImportSummary.summary.inserted}</div>
              <div>包含信息：{ledgerImportSummary.summary.processed}</div>
              <div>跳过行：{ledgerImportSummary.summary.skipped}</div>
            </div>
            {Array.isArray(ledgerImportSummary.summary.errors) && ledgerImportSummary.summary.errors.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>错误详情</div>
                <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 6, padding: '8px 10px', background: '#fff', color: 'var(--text-secondary)' }}>
                  {ledgerImportSummary.summary.errors.map((item, idx) => (
                    <div key={`error-${idx}`} style={{ fontSize: 12, marginBottom: 6 }}>
                      第 {item.row} 行（{item.name || '未匹配姓名'}）：{item.error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
  </WideModal>
      <WideModal
        isOpen={isSpecialModalOpen}
        onClose={handleCloseSpecialOperatorModal}
        title={editingSpecialOperator ? '编辑持证人员资料' : '登记持证人员'}
        size="wide"
      >
        {isLoadingSpecialEmployees ? (
          <div style={{ padding: '1rem 0', fontSize: 14, color: 'var(--text-secondary)' }}>正在加载员工列表...</div>
        ) : (
          <SpecialOperatorForm
            onSubmit={handleSpecialOperatorSubmit}
            onCancel={handleCloseSpecialOperatorModal}
            initialData={editingSpecialOperator}
            employees={specialOperatorEmployees}
          />
        )}
        {isSpecialSubmitting && (
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center' }}>正在保存，请稍候...</div>
        )}
  </WideModal>
          <WideModal
            isOpen={isSafetyModalOpen}
            onClose={handleCloseSafetyManagerModal}
            title={editingSafetyManager ? '编辑安全管理人员资料' : '登记安全管理人员'}
            size="wide"
          >
            {isLoadingSafetyEmployees ? (
              <div style={{ padding: '1rem 0', fontSize: 14, color: 'var(--text-secondary)' }}>正在加载员工列表...</div>
            ) : (
              <SafetyManagerForm
                onSubmit={handleSafetyManagerSubmit}
                onCancel={handleCloseSafetyManagerModal}
                initialData={editingSafetyManager}
                employees={safetyManagerEmployees}
              />
            )}
            {isSafetySubmitting && (
              <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center' }}>正在保存，请稍候...</div>
            )}
          </WideModal>
      <WideModal
        isOpen={pdfPreview.isOpen}
        onClose={handleClosePdfPreview}
        title={pdfPreview.name || '证件预览'}
        size="wide"
      >
        {pdfPreview.url ? (
          <div style={{ height: '70vh' }}>
            <SimplePdfViewer url={pdfPreview.url} fileName={pdfPreview.name} />
          </div>
        ) : (
          <div style={{ padding: '1rem 0', fontSize: 14, color: 'var(--text-secondary)' }}>未找到可预览的文件。</div>
        )}
  </WideModal>
    </div>
  );
};

export default EducationTrainingPage;
